import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { LabelPreview } from "../orders/LabelPreview";

interface Pet {
  id: string;
  name: string;
  motto: string | null;
  active: boolean;
  createdAt: string;
  owner: { id: string; email: string; name: string };
}

// POSIX-sicheres Quoting: in einfache Anführungszeichen, internes ' wird zu '\''
function shellQuote(s: string): string {
  return `'${s.replace(/'/g, "'\\''")}'`;
}

function petLabelCmd(pet: Pet, withName: boolean): string {
  const parts = ["python3 print_petlabel.py", pet.id];
  if (withName && pet.name) parts.push(`--name ${shellQuote(pet.name)}`);
  return parts.join(" ");
}

function CopyButton({ text, label, className }: { text: string; label: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard nicht verfügbar (z. B. ohne HTTPS) — ignorieren */
    }
  };
  return (
    <button type="button" onClick={copy} className={className}>
      {copied ? "Kopiert ✓" : label}
    </button>
  );
}

function PetRow({ pet }: { pet: Pet }) {
  const [withName, setWithName] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  const cmd = petLabelCmd(pet, withName);

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold">
            {pet.name}{" "}
            <code className="text-xs font-normal text-slate-500">{pet.id}</code>
            {!pet.active && <span className="ml-2 text-xs text-amber-600">(inaktiv)</span>}
          </div>
          {pet.motto && <div className="text-sm text-slate-600 italic">„{pet.motto}"</div>}
          <div className="text-sm text-slate-500 mt-1">
            {pet.owner.name} ({pet.owner.email})
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5">
              <input type="checkbox" checked={withName} onChange={(e) => setWithName(e.target.checked)} />
              Name
            </label>
          </div>
          <div className="flex items-center gap-3">
            <CopyButton text={cmd} label="CMD kopieren" className="btn-secondary text-sm" />
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              className="text-sm underline"
            >
              {showPreview ? "Label-Vorschau ausblenden" : "Label-Vorschau"}
            </button>
          </div>
        </div>
      </div>

      {showPreview && (
        <div className="mt-3 max-w-[320px]">
          <LabelPreview catId={pet.id} name={pet.name} showName={withName} width={320} />
        </div>
      )}

      <code className="mt-3 block bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs text-slate-700 break-all">
        {cmd}
      </code>
    </div>
  );
}

export function AdminPetsPage() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  // einfache Debounce für die Suche
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const q = useQuery({
    queryKey: ["admin-pets", debounced],
    queryFn: () => api<Pet[]>(`/api/admin/pets${debounced ? `?search=${encodeURIComponent(debounced)}` : ""}`),
  });

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-4">Haustiere</h1>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Suche nach Name, ID oder Besitzer-E-Mail…"
        className="input mb-4 max-w-md"
      />

      {q.isLoading && <div className="text-slate-500">Lädt…</div>}
      {q.data && q.data.length === 0 && <div className="text-slate-500">Keine Haustiere gefunden.</div>}

      <div className="space-y-3">
        {q.data?.map((pet) => (
          <PetRow key={pet.id} pet={pet} />
        ))}
      </div>
    </div>
  );
}
